import { mappedLeaf } from './leaf-dom'
import { TextCursor, type Doc } from '@w/crdt'
import type { EditorIntent } from '../core/intents'
import type { HistoryTiming } from '../core/history'
import { scopeAddress, scopeBlocks } from '../core/scope'
import { anchoredPoint, textAnchor, type CoreSelection, type CoreState } from '../core/state'
import { findBlock, locate, plain, pointAt, rows } from '../core/tree-read'
import type { CorePoint } from '../core'

/**
 * Where an IME session's text lands, held in the Doc from compositionstart until the session ends, so peer edits
 * move it (design: ime-ownership-design.md). Valid only in the Doc, epoch and scope it was made in, as SavedSelection.
 */
export interface CompositionAnchors {
  doc: Doc
  epoch: number
  scope: string | undefined
  /** The top-level block the session owns: it renders as it was at compositionstart until the session ends. */
  owner: string
  /** The row the marked text is in. */
  target: string
  /** A caret holds the character before it, a range its first replaced character: a peer insertion at either edge
   * stays outside the composed text. */
  start: TextCursor | null
  /** Each replaced character as cursors on its two sides. It is live while they resolve one character apart, so a
   * character a peer deleted is skipped, never resolved onto a survivor and deleted again. */
  replaced: readonly (readonly [left: TextCursor, right: TextCursor])[]
  /** No chars leaf holds an endpoint (an empty row, an atom or break edge): the row, its offsets and its text then.
   * Any change to that row's text cancels the session. */
  row?: { id: string; offset: number; length: number; text: string }
}

/** The anchors of a session over `selection`; null for a selection across rows, which the platform keeps. */
export function anchorComposition(state: CoreState, selection: CoreSelection): CompositionAnchors | null {
  const entries = rows(state.blocks)
  const [from, to] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
    .toSorted((a, b) => a.row - b.row || a.offset - b.offset)
  const entry = entries[from.row]
  if (from.row !== to.row || !entry) return null
  // In a field the scope's block owns the row (a field row is not a document block).
  const owned = state.scope?.blockId ?? entry.block.id
  const owner = scopeBlocks(state.doc).find(top => findBlock([top], owned))
  if (!owner) return null
  const base = { doc: state.doc, epoch: state.doc.version().epoch, scope: state.scope && scopeAddress(state.scope), owner: owner.id,
    target: entry.block.id }
  const replaced: (readonly [TextCursor, TextCursor])[] = []
  for (let offset = from.offset; offset < to.offset; offset++) {
    const left = textAnchor(state.doc, pointAt(entry, offset + 1), 'left')
    if (!left?.toJSON().point) { replaced.length = 0; break }
    replaced.push([left, TextCursor.fromJSON({ ...left.toJSON(), assoc: 'right' })])
  }
  const start = from.offset === to.offset ? textAnchor(state.doc, pointAt(entry, from.offset), 'left') : replaced[0]?.[1] ?? null
  if (start && replaced.length === to.offset - from.offset) return { ...base, start, replaced }
  return { ...base, start: null, replaced: [],
    row: { id: entry.block.id, offset: from.offset, length: to.offset - from.offset, text: plain(entry.block) } }
}

/**
 * The session still has a place in `state`: the same Doc, epoch and scope, its owner at the top level, and its row
 * inside that owner (or the field). A peer deleting or moving the row, or the owner, ends it.
 */
export function compositionLive(state: CoreState, anchors: CompositionAnchors): boolean {
  const { doc } = state
  if (doc !== anchors.doc || doc.version().epoch !== anchors.epoch || (state.scope && scopeAddress(state.scope)) !== anchors.scope) return false
  const owner = scopeBlocks(doc).find(top => top.id === anchors.owner)
  if (!owner) return false
  // In a field the scope's block (the question) must still be inside the owner; outside one, the row itself.
  const member = state.scope ? state.scope.blockId : anchors.target
  return rows(state.blocks).some(entry => entry.block.id === anchors.target) && !!findBlock([owner], member)
}

/** Applies and commits one intent, returning the committed state. */
export type ApplyIntent = (state: CoreState, intent: EditorIntent, timing: HistoryTiming) => CoreState

/**
 * Commits `text` for a finished session through `apply` and returns the row point the text starts at (null: no
 * edit); `false` cancels the session (another Doc, epoch or scope, the owner gone, an endpoint outside it, or its row
 * changed).
 */
export function composedEdits(state: CoreState, anchors: CompositionAnchors, text: string, apply: ApplyIntent, time?: number):
  { row: string; offset: number } | null | false {
  const { doc } = state
  const owner = compositionLive(state, anchors) && scopeBlocks(doc).find(top => top.id === anchors.owner)
  if (!owner) return false
  // A field's blocks are the scope's, not the owner's children.
  const within = state.scope ? state.blocks : [owner]
  const inOwner = (point: CorePoint | null): point is CorePoint => !!point && !!findBlock(within, point.blockId)
  const rowOf = (point: CorePoint) => {
    const entries = rows(state.blocks)
    const at = locate(entries, point)
    return { entry: entries[at.row], offset: at.offset }
  }
  if (anchors.row) {
    const { id, offset, length } = anchors.row
    const entry = rows(state.blocks).find(candidate => candidate.block.id === id)
    if (!entry || !findBlock(within, id) || plain(entry.block) !== anchors.row.text) return false
    if (!text && !length) return null
    const selection = { anchor: pointAt(entry, offset), focus: pointAt(entry, offset + length) }
    apply({ ...state, selection }, text ? { type: 'insertText', text } : { type: 'deleteContentBackward' }, { time })
    return { row: id, offset }
  }
  const live: CorePoint[] = []
  for (const [left, right] of anchors.replaced) {
    const after = anchoredPoint(doc, left), before = anchoredPoint(doc, right)
    if (!after || !before || after.blockId !== before.blockId || after.offset !== before.offset + 1) continue
    if (!inOwner(before)) return false
    live.push(before)
  }
  const start = anchors.start && anchoredPoint(doc, anchors.start)
  if (!inOwner(start)) return false
  const first = rowOf(start)
  const offsets = live.map(point => rowOf(point))
  // One edit when the live replaced characters still run from the start: the common case, which merges with typing.
  if (offsets.every((point, index) => point.entry === first.entry && point.offset === first.offset + index)) {
    if (!text && offsets.length === 0) return null
    const selection = { anchor: pointAt(first.entry, first.offset), focus: pointAt(first.entry, first.offset + offsets.length) }
    apply({ ...state, selection }, text ? { type: 'insertText', text } : { type: 'deleteContentBackward' }, { time })
    return { row: first.entry.block.id, offset: first.offset }
  }
  // A peer insertion inside the range splits it: delete each live character by identity, then insert at the start, as
  // one history step (Yjs deletes items, so the peer's text survives).
  const group = `view:composition:${doc.nextCounter}`
  let current = state
  for (const [left, right] of anchors.replaced.toReversed()) {
    const after = anchoredPoint(current.doc, left), before = anchoredPoint(current.doc, right)
    if (!after || !before || after.blockId !== before.blockId || after.offset !== before.offset + 1) continue
    current = apply({ ...current, selection: { anchor: before, focus: after } }, { type: 'deleteContentBackward' }, { group })
  }
  const at = anchors.start && anchoredPoint(current.doc, anchors.start)
  if (!at) return null
  const entries = rows(current.blocks)
  const found = locate(entries, at)
  if (text) apply({ ...current, selection: { anchor: at, focus: at } }, { type: 'insertText', text }, { group })
  return { row: entries[found.row].block.id, offset: found.offset }
}

/** A bare Text has no leaf element; its row holds native marked text and replacement nodes. */
export function compositionContainer(node: Node, root: HTMLElement): HTMLElement | undefined {
  const leaf = mappedLeaf(node)
  const anchor = node instanceof Element ? node : node.parentElement
  const container = leaf ? leaf.parent : anchor?.closest<HTMLElement>('[data-leaf="true"]') ??
    anchor?.closest<HTMLElement>('[data-core-scope]') ?? anchor?.closest<HTMLElement>('[data-block-id]')
  return container && root.contains(container) ? container : undefined
}
